import { useEffect, useState } from "react";
import { Droplet, Download, X, Share } from "lucide-react";

const DISMISS_KEY = "doh_install_dismissed_at";
const DISMISS_DAYS = 7;

const recentlyDismissed = () => {
  const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return at && Date.now() - at < DISMISS_DAYS * 864e5;
};

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    const ua = window.navigator.userAgent || "";
    const iOS = /iphone|ipad|ipod/i.test(ua) && !window.MSStream;
    if (iOS) {
      setIsIOS(true);
      setVisible(true);
      return;
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    const onInstalled = () => setVisible(false);
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setVisible(false);
    else dismiss();
    setDeferred(null);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed z-50 bottom-20 left-4 right-4 sm:bottom-5 sm:left-5 sm:right-auto sm:max-w-sm surface border-2 rounded-2xl shadow-2xl p-4 float-up"
      style={{ borderColor: "var(--honey-500)" }}
    >
      <button
        data-testid="install-dismiss-btn"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute top-2.5 right-2.5 p-1 rounded-full hover:bg-black/5 text-muted-page"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3 pr-5">
        <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--honey-500)" }}>
          <Droplet className="w-6 h-6" style={{ color: "#3a2a10" }} fill="#3a2a10" />
        </div>
        <div className="min-w-0">
          <p className="font-display text-lg font-bold leading-tight">Install Drops of Honey</p>
          {isIOS ? (
            <p className="text-sm text-muted-page mt-1">
              Tap <Share className="inline w-4 h-4 -mt-0.5 mx-0.5" /> then{" "}
              <span className="font-semibold">Add to Home Screen</span> for a full-screen, one-tap app.
            </p>
          ) : (
            <>
              <p className="text-sm text-muted-page mt-1">Add it to your home screen for faster, app-like access.</p>
              <button
                data-testid="install-accept-btn"
                onClick={install}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-white text-sm transition-transform hover:-translate-y-0.5"
                style={{ background: "var(--brand)" }}
              >
                <Download className="w-4 h-4" /> Install app
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
